import { cn } from "@/lib/utils";
import { useState } from "react";

/**
 * Image
 *
 * Displays an image responsively using <picture>, multiple renditions, and Tailwind CSS.
 * - Accepts multiple renditions of the same image (different widths and/or formats)
 * - Generates proper srcset per MIME format and a sizes attribute for responsive loading
 * - Provides optional aspect-ratio to reduce CLS
 * - Supports lazy loading by default, with a simple blurred/solid placeholder
 *
 * Example usage:
 * <Image
 *   alt="Sunset cliffs"
 *   renditions={[
 *     { url: "/img/sunset-400.avif", width: 400, format: "avif" },
 *     { url: "/img/sunset-800.avif", width: 800, format: "avif" },
 *     { url: "/img/sunset-1200.avif", width: 1200, format: "avif" },
 *     { url: "/img/sunset-400.webp", width: 400, format: "webp" },
 *     { url: "/img/sunset-800.webp", width: 800, format: "webp" },
 *     { url: "/img/sunset-1200.webp", width: 1200, format: "webp" },
 *     { url: "/img/sunset-400.jpg", width: 400, format: "jpeg" },
 *     { url: "/img/sunset-800.jpg", width: 800, format: "jpeg" },
 *     { url: "/img/sunset-1200.jpg", width: 1200, format: "jpeg" },
 *   ]}
 *   sizes="(min-width: 1024px) 900px, 100vw"
 *   className="w-full rounded-2xl shadow"
 * />
 */

export type ImageRendition = {
  /** URL for this rendition */
  url: string;
  /** Intrinsic width in CSS pixels */
  width: number;
  /** Optional intrinsic height; helpful for width/height attrs */
  height?: number;
  /** Format ex: "jpeg" | "png" | "webp" */
  format?: string;
};

export type ImageProps = {
  /** Meaningful alternative text */
  alt: string;
  /** Array of renditions (same image, different sizes and/or formats) */
  renditions: ImageRendition[];
  /** CSS sizes attribute; defaults to 100vw */
  sizes?: string;
  /** Tailwind classes applied to the <img> element */
  className?: string;
  /** If true, disable lazy loading (eager). Default false. */
  priority?: boolean;
  /** Decoding hint */
  decoding?: "auto" | "sync" | "async";
  /** Optional transparent/blur placeholder background (data URL or color) */
  placeholder?: string; // e.g. 'data:jpeg;base64,...' or '#f3f4f6'
  /** Pass-through for ref to the underlying <img> element */
} & React.ComponentPropsWithoutRef<"img">;

function groupByFormat(
  renditions: ImageRendition[]
): Record<string, ImageRendition[]> {
  const byFormat: Record<string, ImageRendition[]> = {};
  renditions.forEach((r) => {
    const key = r.format || "jpeg"; // assume jpeg if not provided
    if (!byFormat[key]) byFormat[key] = [];
    byFormat[key].push(r);
  });
  for (const key of Object.keys(byFormat)) {
    byFormat[key].sort((a, b) => a.width - b.width);
  }
  return byFormat;
}

function pickFallback(renditions: ImageRendition[]): ImageRendition {
  // Prefer JPEG/PNG for <img> fallback; otherwise the largest available
  const preferred = renditions
    .filter((r) => !r.format || r.format === "jpeg" || r.format === "png")
    .sort((a, b) => b.width - a.width);
  if (preferred.length) return preferred[0];
  return [...renditions].sort((a, b) => b.width - a.width)[0];
}

export function Image({
  renditions,
  sizes = "100vw",
  className = "",
  priority = false,
  decoding = "async",
  placeholder,
  ...imgProps
}: ImageProps) {
  const [loaded, setLoaded] = useState(false);

  const byFormat = groupByFormat(renditions);
  const fallback = pickFallback(renditions);
  const largest = renditions.reduce<ImageRendition | null>((acc, r) => {
    if (!acc || r.width > acc.width) return r;
    return acc;
  }, null);

  const width = largest?.width;
  const height = largest?.height;

  return (
    <figure className="relative w-full overflow-hidden">
      {/* Visual placeholder layer */}
      <div
        aria-hidden
        className={cn(
          "absolute inset-0 transition-opacity duration-500 pointer-events-non bg-gray-100",
          loaded ? "opacity-0" : "opacity-100"
        )}
      />

      <picture>
        {/* Emit <source> for each format (AVIF first, then WebP, etc.) */}
        {Object.entries(byFormat)
          .sort(([a], [b]) => {
            // Simple ordering: AVIF -> WebP -> JPEG/PNG -> others
            const order = (t: string) =>
              t.includes("avif")
                ? 0
                : t.includes("webp")
                ? 1
                : t.includes("jpeg") || t.includes("png")
                ? 2
                : 3;
            return order(a) - order(b);
          })
          .map(([format, list]) => (
            <source
              key={format}
              type={`image/${format}`}
              srcSet={list.map((r) => `${r.url} ${r.width}w`).join(", ")}
              sizes={sizes}
            />
          ))}

        {/* Fallback <img> */}
        <img
          className={`block h-auto w-full ${className}`}
          src={fallback.url}
          loading={priority ? "eager" : "lazy"}
          decoding={decoding}
          sizes={sizes}
          srcSet={
            byFormat[fallback.format || "jpeg"]
              ?.map((r) => `${r.url} ${r.width}w`)
              .join(", ") || undefined
          }
          width={width}
          height={height}
          onLoad={() => setLoaded(true)}
          {...imgProps}
        />
      </picture>
    </figure>
  );
}
